import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { getRowerColorByKey } from '../../../../utils/rowerColors';
import type { RowerContext } from '../../../../types/rowerContext';
import { uiBtn } from '../../../ui/buttonClasses';

interface GateForceHeaderControlsProps {
  tracesActive: boolean;
  averageActive: boolean;
  crewActive: boolean;
  availableRowers: string[];
  currentRower: string;
  hiddenRowersCount: number;
  hasRowerContext?: boolean;
  isFilterMode?: boolean;
  onTracesToggle: () => void;
  onAverageToggle: () => void;
  onCrewToggle: () => void;
  onCurrentRowerChange: (rower: string) => void;
  onResetFilters: () => void;
}

export const GateForceHeaderControls: React.FC<GateForceHeaderControlsProps> = ({
  tracesActive,
  averageActive,
  crewActive,
  availableRowers,
  currentRower,
  hiddenRowersCount,
  hasRowerContext = false,
  isFilterMode = false,
  onTracesToggle,
  onAverageToggle,
  onCrewToggle,
  onCurrentRowerChange,
  onResetFilters,
}) => {
  const btnStyle: React.CSSProperties = {
    fontSize: '0.8125rem',
    padding: '0.3rem 0.625rem',
  };

  const selectStyle: React.CSSProperties = {
    ...btnStyle,
    paddingRight: '1.5rem',
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {!hasRowerContext && !crewActive && availableRowers.length > 1 && (
        <select
          value={currentRower}
          onChange={(e) => {
            onCurrentRowerChange(e.target.value);
            e.target.blur();
          }}
          className={`${uiBtn('toggle', { size: 'sm' })} filter-control min-w-[8rem]`}
          style={selectStyle}
        >
          {availableRowers.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      )}

      <button
        type="button"
        onClick={onTracesToggle}
        className={`${uiBtn('toggle', { pressed: tracesActive && !crewActive, size: 'sm' })}`}
        style={btnStyle}
        aria-pressed={tracesActive && !crewActive}
      >
        Traces
      </button>
      <button
        type="button"
        onClick={onAverageToggle}
        className={`${uiBtn('toggle', { pressed: averageActive, size: 'sm' })}`}
        style={btnStyle}
        aria-pressed={averageActive}
      >
        Profile
      </button>

      {!isFilterMode && (
        <>
          <div className="h-4 w-px bg-rowing-gray-200" />

          <button
            type="button"
            onClick={onCrewToggle}
            className={`${uiBtn('toggle', { pressed: crewActive, size: 'sm' })}`}
            style={btnStyle}
            aria-pressed={crewActive}
          >
            Crew
          </button>
        </>
      )}

      {hiddenRowersCount > 0 && (
        <button
          type="button"
          onClick={onResetFilters}
          className={`${uiBtn('outline', { size: 'sm' })}`}
          style={btnStyle}
        >
          Reset
        </button>
      )}

      <GateForceInfoPopover btnStyle={btnStyle} />
    </div>
  );
};

const GateForceInfoPopover: React.FC<{ btnStyle: React.CSSProperties }> = ({ btnStyle }) => {
  const [showInfo, setShowInfo] = useState(false);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const infoPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        infoButtonRef.current &&
        !infoButtonRef.current.contains(e.target as Node) &&
        infoPopoverRef.current &&
        !infoPopoverRef.current.contains(e.target as Node)
      ) {
        setShowInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInfo]);

  return (
    <div className="relative">
      <button
        ref={infoButtonRef}
        type="button"
        onClick={() => setShowInfo((prev) => !prev)}
        className={uiBtn('icon', { pressed: showInfo })}
        style={{ padding: btnStyle.padding, width: '2rem', height: '2rem' }}
        title="Shortcuts & help"
        aria-label="Show shortcuts and help"
      >
        <Info style={{ width: 14, height: 14 }} />
      </button>
      {showInfo && (
        <div
          ref={infoPopoverRef}
          className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-xl w-[280px]"
        >
          <div className="p-3 space-y-3 text-xs text-gray-700">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1.5">Keyboard Shortcuts</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Play / Pause</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                    Space
                  </kbd>
                </div>
                <div className="flex justify-between">
                  <span>Reset zoom &amp; animation</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                    R
                  </kbd>
                </div>
                <div className="flex justify-between">
                  <span>Scrub forward / back</span>
                  <div className="flex gap-1">
                    <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                      &larr;
                    </kbd>
                    <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                      &rarr;
                    </kbd>
                  </div>
                </div>
              </div>
            </div>
            <hr className="border-gray-200" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1.5">Display Modes</h4>
              <div className="space-y-1">
                <p>
                  <span className="font-medium">Traces</span> — Individual stroke overlays for a
                  single rower.
                </p>
                <p>
                  <span className="font-medium">Profile</span> — Mean force profile across all
                  strokes.
                </p>
                <p>
                  <span className="font-medium">Crew</span> — Compare session averages across
                  rowers.
                </p>
              </div>
            </div>
            <hr className="border-gray-200" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1.5">Interactions</h4>
              <div className="space-y-1">
                <p>Shift + scroll to zoom &middot; Drag to pan &middot; Shift + drag to box-zoom</p>
                <p className="text-gray-500">
                  Plain scroll moves the page; hold Shift while scrolling over the chart to zoom.
                </p>
                <p>In Crew mode, click a rower in the legend to solo it</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface GateForceRowerLegendProps {
  availableRowers: string[];
  selectedCrewRowers: string[];
  crewActive: boolean;
  focusedRowerKey?: string | null;
  rowerContext?: RowerContext | null;
  onCrewRowerToggle: (rower: string) => void;
}

export const GateForceRowerLegend: React.FC<GateForceRowerLegendProps> = ({
  availableRowers,
  selectedCrewRowers,
  crewActive,
  focusedRowerKey = null,
  rowerContext = null,
  onCrewRowerToggle,
}) => {
  const rowerChips = useMemo(
    () =>
      availableRowers.map((rower) => ({
        rower,
        color: getRowerColorByKey(rower),
      })),
    [availableRowers]
  );

  const isReadOnlyContext = !!rowerContext;
  const showFilterLegend = rowerContext?.mode === 'filter';
  const shouldShowLegend = (crewActive || showFilterLegend) && availableRowers.length > 0;
  if (!shouldShowLegend) return null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-4 pb-2">
      {rowerChips.map(({ rower, color }) => {
        const allVisible = selectedCrewRowers.length === 0;
        const on = allVisible || selectedCrewRowers.includes(rower);
        const isNonFocused = focusedRowerKey !== null && focusedRowerKey !== rower;
        const dimmed = !on || isNonFocused;
        const displayColor = isNonFocused ? 'rgba(100, 100, 100, 0.25)' : color;
        const label =
          rowerContext?.mode === 'filter' && focusedRowerKey === rower
            ? rowerContext.displayName
            : rower.replace('Rower ', 'R');
        return (
          <button
            key={rower}
            type="button"
            onClick={isReadOnlyContext ? undefined : () => onCrewRowerToggle(rower)}
            disabled={isReadOnlyContext}
            className={`flex items-center gap-2 rounded px-1 transition-opacity duration-150 ${
              isReadOnlyContext
                ? 'cursor-default'
                : 'outline-none focus:ring-2 focus:ring-rowing-sky focus:ring-offset-1'
            }`}
            style={{ opacity: dimmed ? 0.3 : 1 }}
            aria-label={
              isReadOnlyContext
                ? `${label} legend`
                : `Toggle ${label}, currently ${dimmed ? 'hidden' : 'visible'}`
            }
          >
            <span
              className="inline-block rounded-full border-2 flex-shrink-0"
              style={{
                width: 12,
                height: 12,
                borderColor: displayColor,
                backgroundColor: '#fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            />
            <span
              className="text-xs font-medium text-rowing-gray-700 select-none"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
